// Admin Control Center UI kit — official identity (navy · beige · white)
import { useState, useEffect } from "react";
import { X, Upload, Loader2, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { GlassCard, CountUp, Chip, Bar, Skeleton } from "@/v2/components/ui";
import { uploadFile } from "@/v2/lib/files";

export { GlassCard, CountUp, Chip, Bar, Skeleton };

export const tip = {
  contentStyle: { background: "#ffffff", border: "1px solid #e3e7ea", borderRadius: 12, color: "#25343f", fontSize: 12 },
  labelStyle: { color: "#66717a" },
};

export const AV_FALLBACK = (e) => {
  e.currentTarget.src = "https://api.dicebear.com/9.x/initials/svg?seed=User&backgroundColor=0A4D3C";
};

export const PageHead = ({ icon: I, title, sub, children }) => (
  <div className="flex items-end justify-between flex-wrap gap-3">
    <div>
      <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
        {I && <I className="text-beige-ink" size={24} strokeWidth={1.75} />} {title}
      </h1>
      {sub && <p className="text-ink-2 mt-1 text-sm">{sub}</p>}
    </div>
    {children}
  </div>
);

export const Modal = ({ title, onClose, children, wide = false, testId = "v2-modal" }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="v2-overlay flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`v2-glass v2-glass-gold w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-8 p-6`}
        onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-extrabold">{title}</h3>
          <button onClick={onClose} data-testid={`${testId}-close`}
            className="w-9 h-9 rounded-full v2-inset flex items-center justify-center hover:opacity-80"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Confirm = ({ title, body, confirmLabel = "تأكيد", danger = false, onConfirm, onClose, testId = "v2-confirm" }) => (
  <Modal title={title} onClose={onClose} testId={testId}>
    <p className="text-ink-2 text-sm mb-6 leading-relaxed">{body}</p>
    <div className="flex gap-3">
      <button onClick={onConfirm} data-testid={`${testId}-yes`} className={danger ? "v2-btn-danger flex-1" : "v2-btn flex-1"}>{confirmLabel}</button>
      <button onClick={onClose} data-testid={`${testId}-no`} className="v2-btn-ghost flex-1">إلغاء</button>
    </div>
  </Modal>
);

export const Toggle = ({ checked, onChange, label, testId }) => (
  <div className="flex items-center justify-between gap-3 v2-inset p-3">
    <span className="text-xs font-bold">{label}</span>
    <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
      className="relative w-11 h-6 rounded-full transition-colors shrink-0"
      style={{ background: checked ? "#006654" : "rgba(255,255,255,0.14)" }}>
      <span className="absolute top-0.5 w-5 h-5 rounded-full bg-surface transition-all"
        style={{ right: checked ? "2px" : "22px" }} />
    </button>
  </div>
);

export const Table = ({ head, children, min = 720, testId }) => (
  <GlassCard className="overflow-x-auto" data-testid={testId}>
    <table className="v2-table" style={{ minWidth: min }}>
      <thead><tr>{head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </GlassCard>
);

export const Kpi = ({ icon: I, label, value, suffix = "", tone = "emerald", testId }) => {
  const tones = {
    emerald: ["#006654", "rgba(0,102,84,0.10)"],
    gold: ["#bb9661", "rgba(187,150,97,0.14)"],
    red: ["#a83e1e", "rgba(168,62,30,0.10)"],
    muted: ["#66717a", "#e3e7ea"],
  };
  const [c] = tones[tone] || tones.emerald;
  return (
    <GlassCard hover className="p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-sm text-ink-3"><I size={15} strokeWidth={1.8} style={{ color: c }} aria-hidden="true" />{label}</div>
      <div className="text-[1.9rem] leading-tight font-semibold tabular-nums mt-1.5"><CountUp value={value} />{suffix}</div>
    </GlassCard>
  );
};

export const Pagination = ({ page, pages, onChange }) => pages > 1 ? (
  <div className="flex items-center justify-center gap-3">
    <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1}
      data-testid="v2-page-prev" className="v2-btn-ghost v2-btn-sm disabled:opacity-40"><ChevronRight size={15} /> السابق</button>
    <span className="text-sm text-ink-2">صفحة {page} من {pages}</span>
    <button onClick={() => onChange(Math.min(pages, page + 1))} disabled={page === pages}
      data-testid="v2-page-next" className="v2-btn-ghost v2-btn-sm disabled:opacity-40">التالي <ChevronLeft size={15} /></button>
  </div>
) : null;

export const UploadBtn = ({ accept, label = "رفع ملف", onDone, testId, className = "v2-btn-ghost v2-btn-sm" }) => {
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const res = await uploadFile(file, setPct);
      toast.success("تم رفع الملف بنجاح");
      onDone?.(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || "تعذّر رفع الملف");
    } finally { setPct(null); }
  };
  return (
    <label className={`${className} cursor-pointer inline-flex items-center gap-2`} data-testid={testId}>
      {pct === null ? <><Upload size={14} /> {label}</> : <><Loader2 size={14} className="animate-spin" /> {pct}%</>}
      <input type="file" accept={accept} onChange={pick} className="hidden" disabled={pct !== null} />
    </label>
  );
};

export const Empty = ({ text }) => (
  <GlassCard className="p-10 text-center text-ink-2 text-sm">{text}</GlassCard>
);

